import { Helmet } from 'react-helmet-async';
import { Link, useParams, Navigate } from 'react-router-dom';
import { PageHeader } from '../components/sections/PageHeader';
import { ArrowLeft, ArrowRight, Calendar, Clock, Tag } from 'lucide-react';
import '../styles/home.css';

// Articles de blog avec contenu complet
const blogArticles = [
  {
    id: 'naturopathie-digestion-angouleme',
    title: 'Naturopathie et digestion : soulager ballonnements et inconfort à Angoulême',
    description: 'Découvrez comment la naturopathie peut vous aider à retrouver un confort digestif durable. Conseils naturels de votre naturopathe à Angoulême.',
    metaTitle: 'Naturopathie et digestion à Angoulême - Laura Body Naturopathe',
    metaDescription: 'Découvrez comment la naturopathie peut vous aider à soulager ballonnements, reflux et troubles digestifs. Consultations à Angoulême avec Laura Body.',
    date: '11 juillet 2025',
    readTime: '5 min',
    content: `Les troubles digestifs touchent une grande partie de la population : ballonnements, reflux gastriques, constipation, syndrome de l'intestin irritable... Ces désagréments quotidiens impactent significativement la qualité de vie.

La naturopathie propose une approche globale pour comprendre et traiter les causes profondes de ces déséquilibres digestifs.

## Les causes fréquentes des troubles digestifs

• Alimentation inadaptée ou déséquilibrée
• Stress chronique qui perturbe le système nerveux entérique
• Déséquilibre du microbiote intestinal (flore intestinale)
• Intolérances alimentaires non identifiées
• Mastication insuffisante et repas pris trop rapidement
• Sédentarité

## L'accompagnement naturopathique pour la digestion

Lors d'une consultation en naturopathie à Angoulême, Laura Body réalise un bilan complet de votre hygiène de vie et de vos habitudes alimentaires. L'objectif est d'identifier les facteurs perturbateurs et de mettre en place des solutions personnalisées.

### Les axes de travail incluent :

• Réforme alimentaire progressive et adaptée à vos besoins
• Introduction d'aliments favorisant le microbiote (prébiotiques, probiotiques naturels)
• Phytothérapie ciblée (mélisse, fenouil, artichaut, chardon-marie...)
• Techniques de gestion du stress pour apaiser le système nerveux
• Conseils d'hygiène de vie (horaires des repas, mastication, activité physique)

## La réflexologie en complément

La réflexologie plantaire est un excellent complément : en stimulant les zones réflexes liées au système digestif, elle favorise le bon fonctionnement des organes d'élimination et apaise les tensions abdominales.

Consultez Laura Body, naturopathe à Angoulême, pour retrouver un confort digestif durable et naturel. Cabinet situé au 116 rue de Saintes, 16000 Angoulême.`,
    tags: ['naturopathie', 'digestion', 'Angoulême']
  },
  {
    id: 'naturopathie-cycle-feminin-angouleme',
    title: 'Accompagnement naturopathique du cycle féminin à Angoulême',
    description: 'SPM, règles douloureuses, ménopause : la naturopathie offre des solutions naturelles pour accompagner les femmes à chaque étape de leur vie.',
    metaTitle: 'Naturopathie et cycle féminin à Angoulême - Laura Body',
    metaDescription: 'SPM, règles douloureuses, ménopause : accompagnement naturopathique du cycle féminin à Angoulême. Consultations avec Laura Body, naturopathe.',
    date: '11 juillet 2025',
    readTime: '6 min',
    content: `Le corps féminin traverse de nombreuses étapes hormonales tout au long de la vie : puberté, cycles menstruels, grossesse, post-partum, périménopause et ménopause. La naturopathie propose un accompagnement naturel et respectueux pour chacune de ces phases.

## Les troubles du cycle féminin fréquemment rencontrés

• Syndrome prémenstruel (SPM) : irritabilité, douleurs, rétention d'eau
• Règles douloureuses (dysménorrhée)
• Cycles irréguliers
• Endométriose et SOPK (accompagnement complémentaire)
• Troubles de la périménopause : bouffées de chaleur, troubles du sommeil, prise de poids
• Fatigue chronique liée aux variations hormonales

## L'approche naturopathique du bien-être féminin

Laura Body, naturopathe à Angoulême, vous accompagne avec une approche globale qui prend en compte votre terrain, votre mode de vie et vos besoins spécifiques.

### Les piliers de l'accompagnement

**Alimentation hormonale** : Certains aliments soutiennent naturellement l'équilibre hormonal. Le naturopathe vous guide vers une alimentation adaptée à chaque phase du cycle.

**Phytothérapie féminine** : Des plantes comme le gattilier, l'alchémille, la sauge ou l'onagre sont reconnues pour leur action régulatrice sur le système hormonal féminin.

**Gestion du stress** : Le stress impacte directement les hormones. Des techniques de relaxation et de respiration sont proposées.

**Hygiène de vie** : Sommeil, activité physique adaptée, gestion des perturbateurs endocriniens...

## Le Massage Rose Équilibre

Le Massage Rose Équilibre, exclusivement réservé aux femmes, est particulièrement adapté pour accompagner ces périodes de transition. L'huile essentielle de Rose de Damas et le quartz rose favorisent l'apaisement émotionnel et le rééquilibrage.

Prenez rendez-vous avec Laura Body, naturopathe spécialisée dans l'accompagnement féminin à Angoulême. Cabinet au 116 rue de Saintes.`,
    tags: ['naturopathie', 'femmes', 'hormones', 'Angoulême']
  },
  {
    id: 'renforcer-immunite-naturellement-angouleme',
    title: 'Renforcer son immunité naturellement avec la naturopathie à Angoulême',
    description: 'Préparez votre corps à affronter l\'hiver grâce à la naturopathie. Conseils naturels pour booster votre système immunitaire à Angoulême.',
    metaTitle: 'Renforcer son immunité naturellement à Angoulême - Naturopathie',
    metaDescription: 'Boostez votre système immunitaire avec la naturopathie à Angoulême. Conseils naturels et accompagnement personnalisé avec Laura Body.',
    date: '11 juillet 2025',
    readTime: '5 min',
    content: `Le système immunitaire est notre bouclier naturel contre les infections. Pourtant, fatigue, stress, alimentation déséquilibrée ou manque de sommeil peuvent l'affaiblir. La naturopathie propose des solutions naturelles pour renforcer vos défenses immunitaires de manière durable.

## Les facteurs qui affaiblissent l'immunité

• Stress chronique et surmenage
• Sommeil insuffisant ou de mauvaise qualité
• Alimentation pauvre en nutriments essentiels
• Sédentarité ou excès de sport intensif
• Déséquilibre du microbiote intestinal (80% de l'immunité se joue dans l'intestin !)
• Exposition aux toxines environnementales

## L'approche naturopathique pour booster l'immunité

Lors d'une consultation en naturopathie à Angoulême, Laura Body évalue votre terrain et identifie les points faibles de votre système immunitaire.

### Les leviers naturels pour renforcer vos défenses

**Alimentation immunostimulante** : Vitamines C et D, zinc, sélénium, antioxydants... Certains aliments sont de véritables alliés de l'immunité. Le naturopathe vous guide vers une alimentation ciblée.

**Soutien du microbiote** : Un intestin en bonne santé est la clé d'une immunité forte. Probiotiques naturels, aliments fermentés et prébiotiques sont au programme.

**Phytothérapie et aromathérapie** : Échinacée, propolis, sureau, thym, ravintsara... La nature regorge de plantes aux propriétés immunostimulantes.

**Gestion du stress** : Le stress chronique épuise le système immunitaire. Des techniques de relaxation sont proposées.

**Optimisation du sommeil** : C'est pendant le sommeil que le corps se régénère et que l'immunité se renforce.

## La réflexologie en complément

La réflexologie plantaire complète parfaitement cet accompagnement en stimulant les zones réflexes liées au système lymphatique et aux organes d'élimination.

Préparez l'automne et l'hiver sereinement ! Consultez Laura Body, naturopathe à Angoulême, pour un bilan personnalisé. Cabinet au 116 rue de Saintes, 16000 Angoulême.`,
    tags: ['naturopathie', 'immunité', 'prévention', 'Angoulême']
  },
  {
    id: 'stress-anxiete-solutions-naturelles-angouleme',
    title: 'Stress et anxiété : les solutions naturelles à Angoulême',
    description: 'Découvrez comment la naturopathie, la réflexologie et le massage peuvent vous aider à gérer le stress et l\'anxiété naturellement à Angoulême.',
    metaTitle: 'Stress et anxiété : solutions naturelles à Angoulême - Laura Body',
    metaDescription: 'Gérez le stress et l\'anxiété naturellement grâce à la naturopathie, la réflexologie et le massage à Angoulême. Consultations avec Laura Body.',
    date: '11 juillet 2025',
    readTime: '7 min',
    content: `Le stress et l'anxiété sont devenus des maux omniprésents dans notre société moderne. Pression professionnelle, charge mentale, incertitudes... Ces tensions chroniques impactent notre santé physique et mentale. La naturopathie offre des solutions naturelles et durables pour retrouver sérénité et équilibre.

## Les manifestations du stress chronique

• Tensions musculaires (nuque, épaules, dos)
• Troubles du sommeil et fatigue persistante
• Difficultés de concentration et irritabilité
• Troubles digestifs (ventre noué, ballonnements)
• Palpitations et oppression thoracique
• Affaiblissement du système immunitaire
• Problèmes de peau (eczéma, psoriasis aggravés par le stress)

## L'approche naturopathique de la gestion du stress

Laura Body, naturopathe à Angoulême, vous accompagne avec une approche globale pour comprendre les sources de votre stress et mettre en place des solutions adaptées.

### Les piliers de l'accompagnement anti-stress

**Alimentation anti-stress** : Certains nutriments (magnésium, vitamines B, oméga-3, tryptophane) soutiennent le système nerveux. Le naturopathe vous guide vers une alimentation apaisante.

**Phytothérapie relaxante** : Passiflore, valériane, aubépine, rhodiola, ashwagandha... De nombreuses plantes sont reconnues pour leurs propriétés calmantes et adaptogènes.

**Techniques de respiration et relaxation** : Des exercices simples à pratiquer au quotidien pour calmer le système nerveux.

**Hygiène de vie** : Optimisation du sommeil, activité physique adaptée, gestion des écrans et des stimulants.

## Les soins complémentaires proposés par Laura Body

**La réflexologie plantaire** agit directement sur le système nerveux en stimulant les zones réflexes liées à la détente. Elle procure un apaisement profond et durable.

**Le massage Shirotchampi**, massage ayurvédique du crâne, du visage et des épaules, est particulièrement efficace pour libérer les tensions cervicales et calmer le mental.

**Le Massage Rose Équilibre** (réservé aux femmes) favorise un profond lâcher-prise grâce à l'huile essentielle de Rose de Damas, reconnue pour ses vertus apaisantes.

Ne laissez plus le stress diriger votre vie ! Consultez Laura Body, naturopathe et réflexologue à Angoulême. Cabinet situé au 116 rue de Saintes, 16000 Angoulême. Rendez-vous sur Perfactive ou au 06 50 15 59 23.`,
    tags: ['stress', 'anxiété', 'naturopathie', 'réflexologie', 'Angoulême']
  },
  {
    id: 'bienfaits-reflexologie-plantaire',
    title: 'Les bienfaits de la réflexologie plantaire',
    description: 'Découvrez comment la réflexologie plantaire agit sur votre corps et votre esprit pour restaurer l\'équilibre naturel.',
    metaTitle: 'Bienfaits de la réflexologie plantaire - Laura Body Angoulême',
    metaDescription: 'Découvrez les bienfaits de la réflexologie plantaire : gestion du stress, circulation, sommeil. Séances à Angoulême avec Laura Body, réflexologue certifiée.',
    date: '10 juillet 2025',
    readTime: '4 min',
    content: `La réflexologie plantaire est une technique ancestrale qui consiste à stimuler des zones réflexes situées sous les pieds. Chaque zone correspond à un organe ou une partie du corps.

En exerçant des pressions spécifiques sur ces points, le réflexologue favorise la détente, stimule les capacités d'auto-régulation du corps et contribue à rétablir l'équilibre énergétique.

## Les principaux bienfaits de la réflexologie plantaire

• Gestion du stress et réduction de l'anxiété
• Amélioration de la circulation sanguine
• Harmonisation des fonctions vitales
• Soulagement des troubles du sommeil
• Réduction des tensions musculaires et du dos
• Activation du système lymphatique
• Soulagement des migraines et maux de tête

## Comment se déroule une séance ?

À Angoulême, Laura Body propose des séances de réflexologie plantaire personnalisées d'une durée d'1 heure, dans un cadre apaisant au 116 rue de Saintes.

La séance commence par un échange sur vos besoins et votre état de santé général. Vous êtes ensuite installé(e) confortablement et Laura travaille sur les zones réflexes de vos pieds avec des techniques de pression adaptées.

Prenez rendez-vous pour découvrir les bienfaits de la réflexologie plantaire à Angoulême.`,
    tags: ['réflexologie', 'bien-être', 'Angoulême']
  },
  {
    id: 'naturopathie-troubles-cutanes',
    title: 'Naturopathie et troubles cutanés : acné, eczéma, psoriasis',
    description: 'Comment la naturopathie peut vous aider à soulager vos problèmes de peau de manière naturelle et durable.',
    metaTitle: 'Naturopathie et troubles cutanés à Angoulême - Laura Body',
    metaDescription: 'Acné, eczéma, psoriasis : la naturopathie offre des solutions naturelles pour vos troubles cutanés. Consultations à Angoulême avec Laura Body.',
    date: '10 juillet 2025',
    readTime: '5 min',
    content: `Les troubles cutanés comme l'acné, l'eczéma, le psoriasis ou la rosacée touchent des millions de personnes. La naturopathie propose une approche globale pour comprendre et traiter ces déséquilibres en profondeur.

## Une approche globale de la peau

Plutôt que de traiter uniquement les symptômes visibles, le naturopathe cherche à identifier la cause profonde du déséquilibre : alimentation, stress, déséquilibre hormonal, toxines accumulées, déséquilibre du microbiote…

## L'accompagnement proposé par Laura Body

Laura Body, naturopathe à Angoulême spécialisée dans les troubles cutanés, vous propose un accompagnement complet :

• Bilan personnalisé de votre hygiène de vie
• Conseils nutritionnels ciblés pour la santé de votre peau
• Phytothérapie et aromathérapie adaptées
• Gestion du stress et des émotions
• Suivi régulier pour des résultats durables

## Le Pack Cutané

Le Pack Cutané combine 4 séances de naturopathie et 4 séances de réflexologie pour un accompagnement optimal de vos troubles cutanés. Cette approche combinée permet d'agir sur tous les fronts : alimentation, élimination, stress et équilibre général.

Consultez Laura Body à Angoulême pour retrouver une peau saine naturellement.`,
    tags: ['naturopathie', 'peau', 'troubles cutanés']
  },
  {
    id: 'naturopathie-sommeil-stress',
    title: 'Naturopathie et sommeil : retrouver un sommeil réparateur',
    description: 'Les solutions naturelles de la naturopathie pour améliorer la qualité de votre sommeil et réduire le stress.',
    metaTitle: 'Naturopathie et sommeil à Angoulême - Laura Body Naturopathe',
    metaDescription: 'Troubles du sommeil ? La naturopathie offre des solutions naturelles pour retrouver un sommeil réparateur. Consultations à Angoulême avec Laura Body.',
    date: '10 juillet 2025',
    readTime: '5 min',
    content: `Le sommeil est un pilier fondamental de notre santé. Pourtant, de nombreuses personnes souffrent de troubles du sommeil : difficultés d'endormissement, réveils nocturnes, sommeil non réparateur…

La naturopathie offre des solutions naturelles et efficaces pour retrouver un sommeil de qualité.

## Alimentation et sommeil

Certains aliments favorisent la production de mélatonine (hormone du sommeil). Le naturopathe vous guide vers une alimentation propice à un bon sommeil : timing des repas, aliments à privilégier le soir, nutriments essentiels...

## Phytothérapie pour le sommeil

Des plantes comme la valériane, la passiflore ou le tilleul sont reconnues pour leurs propriétés apaisantes et favorisant l'endormissement. Laura Body vous conseille les plantes adaptées à votre situation.

## Gestion du stress

Le stress est l'ennemi n°1 du sommeil. La naturopathie propose des techniques de relaxation, de respiration et de gestion émotionnelle pour calmer le mental avant le coucher.

## Hygiène de vie

Horaires réguliers, exposition à la lumière, activité physique adaptée… Le naturopathe vous accompagne dans la mise en place de bonnes habitudes propices au sommeil.

## La réflexologie pour le sommeil

La réflexologie plantaire est également un excellent complément : en stimulant les zones réflexes liées au système nerveux, elle favorise la détente profonde et un sommeil plus réparateur.

Consultez Laura Body, naturopathe à Angoulême, pour un accompagnement personnalisé.`,
    tags: ['naturopathie', 'sommeil', 'stress']
  },
  {
    id: 'massage-shirotchampi-bienfaits',
    title: 'Le massage Shirotchampi : bienfaits et déroulement',
    description: 'Tout savoir sur le massage ayurvédique Shirotchampi proposé par Laura Body à Angoulême.',
    metaTitle: 'Massage Shirotchampi à Angoulême - Laura Body',
    metaDescription: 'Découvrez le massage Shirotchampi, massage ayurvédique du crâne et des épaules. Séances à Angoulême avec Laura Body. Détente profonde garantie.',
    date: '10 juillet 2025',
    readTime: '4 min',
    content: `Le massage Shirotchampi est un massage ayurvédique traditionnel originaire d'Inde. "Shiro" signifie "tête" et "champi" signifie "massage" en hindi. Cette technique millénaire se concentre sur le cuir chevelu, le visage, le cou et les épaules.

## Bienfaits physiques du Shirotchampi

• Amélioration de la santé des cheveux et du cuir chevelu
• Soulagement des maux de tête et migraines
• Détente des tensions musculaires cervicales
• Stimulation de la circulation sanguine
• Soutien des processus de détoxification

## Bienfaits mentaux et émotionnels

• Réduction du stress et de l'anxiété
• Amélioration de la clarté mentale et de la concentration
• État de calme profond et de relaxation
• Sommeil plus réparateur

## Déroulement d'une séance

Lors de la séance chez Laura Body à Angoulême, une huile végétale (coco ou pépins de raisin) est utilisée. La séance dure environ 1 heure dans un cadre chaleureux et apaisant.

## Pour qui ?

Le massage Shirotchampi est particulièrement recommandé pour les personnes souffrant de stress, de tensions cervicales, de maux de tête récurrents ou simplement pour celles qui recherchent un moment de détente profonde.

Réservez votre séance de massage Shirotchampi à Angoulême avec Laura Body.`,
    tags: ['massage', 'Shirotchampi', 'ayurvédique']
  },
  {
    id: 'massage-rose-equilibre-femmes',
    title: 'Massage Rose Équilibre : un soin exclusif réservé aux femmes',
    description: 'Découvrez le Massage Rose Équilibre de Laura Body à Angoulême : huile de Rose de Damas, quartz rose et huiles essentielles pour un bien-être profond.',
    metaTitle: 'Massage Rose Équilibre à Angoulême - Laura Body',
    metaDescription: 'Massage Rose Équilibre : soin exclusif femmes avec huile de Rose de Damas et quartz rose. Séances à Angoulême avec Laura Body.',
    date: '10 juillet 2025',
    readTime: '4 min',
    content: `Le Massage Rose Équilibre est un massage du corps inspiré d'une approche holistique, dont l'objectif est de favoriser un profond sentiment de bien-être, d'harmonie et d'équilibre entre le corps et l'esprit. Ce soin est exclusivement réservé aux femmes.

## Les bienfaits du Massage Rose Équilibre

Ce massage peut contribuer à :
• Apaiser le système nerveux
• Favoriser un profond lâcher-prise
• Retrouver un sentiment d'équilibre intérieur
• Procurer une sensation de réconfort et de sécurité
• Accompagner les périodes de stress ou de fatigue émotionnelle
• Favoriser une meilleure connexion à soi

## Les actifs naturels utilisés

**Huile essentielle de Rose de Damas** : Véritable joyau de l'aromathérapie, reconnue pour favoriser l'apaisement émotionnel et soutenir la confiance en soi.

**Quartz rose** : Symbole de douceur et d'amour de soi, il favorise une sensation de bien-être et de sérénité intérieure.

**Huile essentielle de Pamplemousse** : Son parfum frais et tonique participe à la sensation de jambes légères.

**Huile essentielle de Cyprès** : Ses propriétés tonifiantes sur la circulation veineuse procurent une agréable sensation de légèreté.

## Informations pratiques

La séance dure 1h15 et coûte 80€. Ce soin est idéal pour les femmes traversant des périodes de transition, de stress ou simplement en quête d'un moment de douceur.

Réservez votre Massage Rose Équilibre avec Laura Body à Angoulême pour une parenthèse de douceur et de bien-être.`,
    tags: ['massage', 'femmes', 'Rose de Damas']
  }
];

// Fonction pour obtenir l'article par ID
const getArticleById = (id) => blogArticles.find(article => article.id === id);

// Fonction pour obtenir les articles adjacents (précédent/suivant)
const getAdjacentArticles = (currentId) => {
  const currentIndex = blogArticles.findIndex(article => article.id === currentId);
  return {
    prev: currentIndex > 0 ? blogArticles[currentIndex - 1] : null,
    next: currentIndex < blogArticles.length - 1 ? blogArticles[currentIndex + 1] : null
  };
};

// Fonction pour formater le contenu avec des titres
const formatContent = (content, articleId) => {
  const lines = content.split('\n');
  return lines.map((line, index) => {
    const lineKey = `${articleId}-line-${index}`;
    if (line.startsWith('## ')) {
      return <h2 key={lineKey} className="blog-article-h2">{line.replace('## ', '')}</h2>;
    } else if (line.startsWith('### ')) {
      return <h3 key={lineKey} className="blog-article-h3">{line.replace('### ', '')}</h3>;
    } else if (line.startsWith('**') && line.endsWith('**')) {
      return <p key={lineKey} className="blog-article-bold">{line.replace(/\*\*/g, '')}</p>;
    } else if (line.startsWith('**')) {
      // Line starting with bold text
      const parts = line.split('**');
      return (
        <p key={lineKey}>
          {parts.map((part, i) => 
            i % 2 === 1 ? <strong key={`${lineKey}-bold-${i}`}>{part}</strong> : part
          )}
        </p>
      );
    } else if (line.startsWith('• ')) {
      return <li key={lineKey} className="blog-article-li">{line.replace('• ', '')}</li>;
    } else if (line.trim() === '') {
      return null;
    } else {
      return <p key={lineKey}>{line}</p>;
    }
  }).filter(Boolean);
};

export default function BlogArticlePage() {
  const { articleId } = useParams();
  const article = getArticleById(articleId);
  
  // Rediriger si l'article n'existe pas
  if (!article) {
    return <Navigate to="/blog" replace />;
  }
  
  const { prev, next } = getAdjacentArticles(articleId);
  
  return (
    <div className="home-container">
      <Helmet>
        <title>{article.metaTitle}</title>
        <meta name="description" content={article.metaDescription} />
        <link rel="canonical" href={`https://www.naturopathe-angouleme.fr/blog/${article.id}`} />
        <meta property="og:title" content={article.metaTitle} />
        <meta property="og:description" content={article.metaDescription} />
        <meta property="og:url" content={`https://www.naturopathe-angouleme.fr/blog/${article.id}`} />
        <meta property="og:type" content="article" />
      </Helmet>

      <PageHeader />

      <section className="page-hero-mini">
        <div className="section-container">
          <Link to="/blog" className="back-link" data-testid="back-to-blog">
            <ArrowLeft size={16} /> Retour au blog
          </Link>
        </div>
      </section>

      <article className="blog-article-page">
        <div className="section-container">
          <div className="blog-article-header">
            <div className="blog-article-meta">
              <span className="blog-article-date">
                <Calendar size={14} /> {article.date}
              </span>
              <span className="blog-article-readtime">
                <Clock size={14} /> {article.readTime} de lecture
              </span>
            </div>
            <h1 className="blog-article-title" data-testid="article-title">{article.title}</h1>
            <p className="blog-article-description">{article.description}</p>
            <div className="blog-article-tags">
              {article.tags.map((tag) => (
                <span key={tag} className="blog-tag">
                  <Tag size={12} /> {tag}
                </span>
              ))}
            </div>
          </div>
          
          <div className="blog-article-body" data-testid="article-content">
            {formatContent(article.content, article.id)}
          </div>

          {/* CTA Réservation */}
          <div className="blog-article-cta">
            <h3>Prenez rendez-vous avec Laura Body</h3>
            <p>Naturopathe et réflexologue à Angoulême, je vous accompagne vers un mieux-être naturel et durable.</p>
            <a 
              href="https://perfactive.fr/laura-body/new?people=26749" 
              className="cta-button"
              data-testid="article-cta-button"
            >
              Réserver une séance
            </a>
          </div>

          {/* Navigation entre articles */}
          <nav className="blog-article-nav" data-testid="article-navigation">
            {prev ? (
              <Link to={`/blog/${prev.id}`} className="blog-nav-link blog-nav-prev" data-testid="prev-article">
                <ArrowLeft size={16} />
                <div>
                  <span className="blog-nav-label">Article précédent</span>
                  <span className="blog-nav-title">{prev.title}</span>
                </div>
              </Link>
            ) : <div />}
            
            {next ? (
              <Link to={`/blog/${next.id}`} className="blog-nav-link blog-nav-next" data-testid="next-article">
                <div>
                  <span className="blog-nav-label">Article suivant</span>
                  <span className="blog-nav-title">{next.title}</span>
                </div>
                <ArrowRight size={16} />
              </Link>
            ) : <div />}
          </nav>
        </div>
      </article>
    </div>
  );
}

// Export articles for use in BlogPage
export { blogArticles };
